import "./MDLNewsEditor.scss";
import { useState, useEffect } from "react";
import { Button, Select, Space, Input, message } from "antd";
import MDLArticle from "../../ui/MDLArticle/MDLArticle";
import { articleService, Article, ArticleElement } from "../../services/articleService";

const MDLNewsEditor = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [currentArticle, setCurrentArticle] = useState<Article | null>(null);
  const [title, setTitle] = useState("");
  const [elements, setElements] = useState<ArticleElement[]>([]);

  // Load articles on mount
  useEffect(() => {
    articleService.initializeHardcodedData();
    loadArticles();
  }, []);

  const loadArticles = () => {
    const allArticles = articleService.getAllArticles();
    setArticles(allArticles);
  };

  const handleSelectArticle = (articleId: string) => {
    const article = articleService.getArticleById(articleId);
    if (article) {
      setSelectedArticleId(articleId);
      setCurrentArticle(article);
      setTitle(article.title);
      setElements([...article.elements]);
      setIsEditMode(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedArticleId(null);
    setCurrentArticle(null);
    setTitle("");
    setElements([]);
    setIsEditMode(true);
  };

  const handleAddElement = (type: "header" | "text" | "subheader" | "image") => {
    const newElement: ArticleElement = {
      type,
      content: type === "image" ? "placeholder.png" : "Enter content here...",
    };
    setElements([...elements, newElement]);
  };

  const handleUpdateElement = (index: number, content: string) => {
    const updated = [...elements];
    updated[index].content = content;
    setElements(updated);
  };

  const handleRemoveElement = (index: number) => {
    setElements(elements.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!title.trim()) {
      message.error("Please enter a title");
      return;
    }
    if (elements.length === 0) {
      message.error("Please add at least one element");
      return;
    }

    if (selectedArticleId && currentArticle) {
      // Update existing
      articleService.updateArticle(selectedArticleId, title, elements);
      message.success("Article updated successfully");
    } else {
      // Create new
      articleService.createArticle(title, elements);
      message.success("Article created successfully");
    }

    loadArticles();
    setIsEditMode(false);
  };

  const handleDelete = () => {
    if (!selectedArticleId) return;
    articleService.deleteArticle(selectedArticleId);
    message.success("Article deleted successfully");
    loadArticles();
    setSelectedArticleId(null);
    setCurrentArticle(null);
    setTitle("");
    setElements([]);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  return (
    <div id="mdl-news-editor">
      <div className="editor-header">
        <h2>News Editor</h2>
        <Button
          type={isEditMode ? "default" : "primary"}
          onClick={() => setIsEditMode(!isEditMode)}
        >
          {isEditMode ? "Abbrechen" : "Bearbeiten"}
        </Button>
      </div>

      {isEditMode ? (
        <div className="editor-panel">
          <div className="editor-controls">
            <div className="title-section">
              <label>Article Title</label>
              <Input
                placeholder="Enter article title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="elements-section">
              <h3>Elements</h3>
              <div className="add-element-buttons">
                <Button onClick={() => handleAddElement("header")}>Add Header</Button>
                <Button onClick={() => handleAddElement("subheader")}>Add Subheader</Button>
                <Button onClick={() => handleAddElement("text")}>Add Text</Button>
                <Button onClick={() => handleAddElement("image")}>Add Image</Button>
              </div>

              <div className="elements-list">
                {elements.map((element, index) => (
                  <div key={index} className="element-item">
                    <div className="element-type">{element.type.toUpperCase()}</div>
                    <Input.TextArea
                      value={element.content}
                      onChange={(e) => handleUpdateElement(index, e.target.value)}
                      rows={element.type === "image" ? 1 : 3}
                      placeholder={`Enter ${element.type} content`}
                    />
                    <Button
                      danger
                      onClick={() => handleRemoveElement(index)}
                      style={{ marginTop: "8px" }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="action-buttons">
              <Space>
                <Button type="primary" onClick={handleSave}>
                  Save Article
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
                {selectedArticleId && (
                  <Button danger onClick={handleDelete}>
                    Delete Article
                  </Button>
                )}
              </Space>
            </div>
          </div>

          {elements.length > 0 && (
            <div className="preview-panel">
              <h3>Preview</h3>
              <MDLArticle id="preview" elements={elements} />
            </div>
          )}
        </div>
      ) : (
        <div className="view-panel">
          <div className="article-selector">
            <label>Select Article</label>
            <Select
              placeholder="Choose an article to view"
              value={selectedArticleId}
              onChange={handleSelectArticle}
              options={articles.map((article) => ({
                label: article.title,
                value: article.id,
              }))}
              style={{ width: "100%" }}
            />
            <Button onClick={handleCreateNew} type="primary" style={{ marginTop: "12px" }}>
              Create New Article
            </Button>
          </div>

          {currentArticle && (
            <div className="article-display">
              <h2>{currentArticle.title}</h2>
              <MDLArticle id={currentArticle.id} elements={currentArticle.elements} />
              <div className="article-meta">
                <small>
                  Created: {new Date(currentArticle.createdAt).toLocaleDateString()}
                </small>
                <small>
                  Updated: {new Date(currentArticle.updatedAt).toLocaleDateString()}
                </small>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MDLNewsEditor;
